import * as React from 'react';
import S, { DataSignal } from 's-js';
import * as $ from 'jquery';
import TZDate from 'lib/TZDate';
import { SuperCriticalComponent, SuperCriticalReference } from 'lib/supercritical';
import { LocalizationContext } from 'common/models/LocalizationContext';

import { SplitDateTimePortions } from './progInit';

import 'lib/kendo-loader';

interface BasePickerProps {
    localization : LocalizationContext;
    min? : () => TZDate;
    max? : () => TZDate;
    disabled? : boolean;
    popupZIndex? : number | 'kendo';
}

type InputRef = (e : HTMLInputElement | null) => void;

export interface SinglePickerProps<TValue> extends BasePickerProps {
    value : DataSignal<TValue>;
    tabindex? : number;
    inputRef? : InputRef;
    autofocus? : boolean;
    isAddPortcall? : boolean;
}

export interface DateTimePickerProps<TValue> extends BasePickerProps {
    value : DataSignal<TValue>;
    tabindex? : { date? : number, time? : number };
    inputRef? : { date? : InputRef, time? : InputRef };
    autofocus? : { date? : boolean };
}

export interface ProgInitDateTimePickerProps extends BasePickerProps {
    value : SplitDateTimePortions;
    tabindex? : { date? : number, time? : number };
    inputRef? : { date? : InputRef, time? : InputRef };
    autofocus? : { date? : boolean, time? : boolean };
    isAddPortcall? : boolean;
}

enum PickerKind {
    Date,
    Time
}

abstract class SinglePickerBase<TValue extends TZDate | null> extends SuperCriticalComponent<SinglePickerProps<TValue>> {
    ref : InputRef;

    constructor(
        props : SinglePickerProps<TValue>,
        private readonly pickerKind : PickerKind,
        private readonly allowNullSets : boolean
    ) {
        super(props);
        const opts = {
            pickerKind: this.pickerKind,
            allowNullSets: this.allowNullSets
        };
        this.ref = SuperCriticalReference(this, (props : SinglePickerProps<TValue>, el : HTMLInputElement) => InitPicker(props, el, opts));
    }

    srender(props : SinglePickerProps<TValue>) {
        const ref = (e : HTMLInputElement | null) => {
            this.ref(e);
            props.inputRef && props.inputRef(e);
        };

        return (
            <input
                className={this.pickerKind === PickerKind.Date ? 'portcall-datepicker' : 'portcall-timepicker'}
                tabIndex={props.tabindex}
                ref={ref}
                // DC 09/23/24 -- This won't do anything, i.e. ref overrides the autofocus; see InitPicker() for autoFocus "fix".
                autoFocus={props.autofocus}
            />
        );
    }
}

class InternalDatePicker extends SinglePickerBase<TZDate> {
    constructor(props : SinglePickerProps<TZDate>) {
        super(props, PickerKind.Date, false);
    }
}

class InternalNullableDatePicker extends SinglePickerBase<TZDate | null> {
    constructor(props : SinglePickerProps<TZDate | null>) {
        super(props, PickerKind.Date, true);
    }
}

class InternalTimePicker extends SinglePickerBase<TZDate> {
    constructor(props : SinglePickerProps<TZDate>) {
        super(props, PickerKind.Time, false);
    }
}

class InternalNullableTimePicker extends SinglePickerBase<TZDate | null> {
    constructor(props : SinglePickerProps<TZDate | null>) {
        super(props, PickerKind.Time, true);
    }
}

// TH 05/09/2017 -- BUGFIX. I stumbled on something brittle in our DatePicker and TimePicker controls.
// The issue is that Kendo adds its own DOM wrapper around whichever element you initialize the
// Date(Time)Picker on. This means that the actual DOM contains an extra wrapper layer that doesn't
// correspond to any node in React's virtual DOM.
// That's fine for most use cases, but it's a problem if you transition your view state in such a
// way that React tries to unmount the Date(Time)Picker node from its parent.
//
// So before my bugfix below, calling code could expose the bug by doing something like
//
//   <div>
//       {showDatePicker() && <DatePicker value={value} localization={localization} />}
//   </div>
//
// As long as the user performed a sequence of actions that caused showDatePicker to flip to
// true and then back to false, that'd expose this bug.
// (NOTE: The above calling code is now safe, due to my bugfix below.)
//
// Internally, React would end up calling parentNode.removeChild(childNode), where parentNode
// was the original parent, and childNode was the node that we initialized the Date(Time)Picker
// on. At that point, childNode wouldn't be a child of parentNode anymore but rather a
// great-grandchild (Kendo adds a lot of wrapping). So the removeChild call would throw
//
//   Uncaught DOMException: Failed to execute 'removeChild' on 'Node':
//   The node to be removed is not a child of this node.
//
// That'd leave the DOM in a dirty state.
//
// E.g. I was running into a case on the PendingExports page's ExportConfirmationPopup where the
// effect was that you could open the popup once w/o issue, but cancelling the popup would throw
// the DOMException, and after that, any subsequent time the user tried to open the popup, React
// would fail to reinitialize the DatePicker and it'd be left in a state where it wouldn't respond
// to click events.
//
// The fix is to add our own extra wrapper layer around the Kendo Date(Time)Picker.
// That way, there's an extra DOM layer between the node that React tries to remove and the
// node that Kendo adds its wrapping to.
//
// So before my bugfix, calling code could avoid the buggy behavior by doing
//
//   <div>
//       {showDatePicker() && <span><DatePicker value={value} localization={localization} /></span>}
//   </div>
//
// Just the extra <span> is enough to avoid the bug.
//
// But rather than forcing calling code to be aware of this bug and the need for an extra
// <span>, I'm gonna change our API so that every component this module exports comes wrapped
// up in the extra <span> already. That avoids this bug, even in the first code example above.
//
// Note that our DateTimePicker component was already just a wrapper <span> around a DatePicker
// and a TimePicker. So it was already safe from this bug.
// That probably helps explain why we're just seeing this now.
export const DatePicker = (props : SinglePickerProps<TZDate>) =>
    <span className="portcall-datepicker-wrapper">
        <InternalDatePicker {...props} />
    </span>;

export const NullableDatePicker = (props : SinglePickerProps<TZDate | null>) =>
    <span className="portcall-datepicker-wrapper">
        <InternalNullableDatePicker {...props} />
    </span>;

export const TimePicker = (props : SinglePickerProps<TZDate>) =>
    <span className="portcall-timepicker-wrapper">
        <InternalTimePicker {...props} />
    </span>;

export const NullableTimePicker = (props : SinglePickerProps<TZDate | null>) =>
    <span className="portcall-timepicker-wrapper">
        <InternalNullableTimePicker {...props} />
    </span>;

export const DateTimePicker = ({ tabindex, inputRef, autofocus, ...rest } : DateTimePickerProps<TZDate>) =>
    <span className="portcall-datetimepicker">
        <InternalDatePicker
            tabindex={tabindex?.date}
            inputRef={inputRef?.date}
            autofocus={autofocus?.date}
            {...rest}
        />
        <InternalTimePicker
            tabindex={tabindex?.time}
            inputRef={inputRef?.time}
            {...rest}
        />
    </span>;

export const NullableDateTimePicker = ({ tabindex, inputRef, autofocus, ...rest } : DateTimePickerProps<TZDate | null>) =>
    <span className="portcall-datetimepicker">
        <InternalNullableDatePicker
            tabindex={tabindex?.date}
            inputRef={inputRef?.date}
            autofocus={autofocus?.date}
            {...rest}
        />
        <InternalNullableTimePicker
            tabindex={tabindex?.time}
            inputRef={inputRef?.time}
            {...rest}
        />
    </span>;

export const ProgInitDateTimePicker = ({ value, tabindex, inputRef, autofocus, isAddPortcall, ...rest } : ProgInitDateTimePickerProps) =>
    <span className="portcall-datetimepicker">
        <InternalNullableDatePicker
            value={value.DatePortion}
            tabindex={tabindex?.date}
            inputRef={inputRef?.date}
            autofocus={autofocus?.date}
            isAddPortcall={isAddPortcall}
            {...rest}
        />
        <InternalNullableTimePicker
            value={value.TimePortion}
            tabindex={tabindex?.time}
            inputRef={inputRef?.time}
            autofocus={autofocus?.time}
            isAddPortcall={isAddPortcall}
            {...rest}
        />
    </span>

interface KendoPopupView {
    popup : { wrapper : JQuery };
}

interface KendoPickerOptions {
    format : string;
    parseFormats : string[];
    interval : number;
}

interface KendoPicker {
    min : (date : Date) => void;
    max : (date : Date) => void;
    value : (date? : Date | null) => Date | null;
    readonly : (_readonly : boolean) => void;
    setOptions : (options : Partial<KendoPickerOptions>) => void;
    destroy : () => void;
    options : KendoPickerOptions;
    timeView? : KendoPopupView;
    dateView? : KendoPopupView;
}

interface KendoPickerOpenEvent {
    view? : 'date' | 'time'
}

function InitPicker<TValue extends TZDate | null>(props : SinglePickerProps<TValue>, el : HTMLInputElement, { pickerKind, allowNullSets } : { pickerKind : PickerKind, allowNullSets : boolean }) {
    const { value, min, max, disabled, autofocus, isAddPortcall, localization : {
        tzutils,
        DateFormatDefaults : formatDefaults
    } } = props;

    // TH: Currently not supporting overrides, but could add those here...
    const format = pickerKind === PickerKind.Date ? formatDefaults.dateFormat : formatDefaults.timeFormat,
        parseFormats = pickerKind === PickerKind.Date ? formatDefaults.dateParseFormats : formatDefaults.timeParseFormats;
    
    let setting = false;
    const options = {
        change: function (this : KendoPicker) {
            const
                // Kendo's value
                kVal = this.value(),
                curr = S.sample(value);

            let val : TZDate | null;

            if (kVal) {
                val = new TZDate(0);

                // Use Kendo's value to construct a new TZDate instance. But be careful b/c in general Kendo
                // date-only pickers can't be trusted to preserve time info and vice versa for time-only pickers.
                // In particular:
                // - Date-only pickers set the time portion of their value to 00:00 seemingly anytime the user
                //   modifies the date, or even if the user just opens and closes the calendar widget w/o making
                //   changes.
                // - Time-only pickers set the date portion of their value to today's date when transitioning
                //   from an unset time value to a populated time value.
                // Our policy will be that date-only pickers will modify only the date portion of their value
                // and will leave the time portion as is, and likewise time-only pickers will modify
                // only the time portion of their value. This is especially important for our combined date/time
                // picker since we construct separate date and time pickers internally.

                // Our usual trick for setting month and date together. See e.g. tzutils.tzify method.
                val.setDate(1);
                val.setFullYear    (pickerKind === PickerKind.Time && curr ? curr.getFullYear    () : kVal.getFullYear    ());
                val.setMonth       (pickerKind === PickerKind.Time && curr ? curr.getMonth       () : kVal.getMonth       ());
                val.setDate        (pickerKind === PickerKind.Time && curr ? curr.getDate        () : kVal.getDate        ());
                val.setHours       (pickerKind === PickerKind.Date && curr ? curr.getHours       () : kVal.getHours       ());
                val.setMinutes     (pickerKind === PickerKind.Date && curr ? curr.getMinutes     () : kVal.getMinutes     ());
                val.setSeconds     (pickerKind === PickerKind.Date && curr ? curr.getSeconds     () : kVal.getSeconds     ());
                val.setMilliseconds(pickerKind === PickerKind.Date && curr ? curr.getMilliseconds() : kVal.getMilliseconds());

                // IMPORTANT: Set timezone offset when done, making sure to preserve walltime.
                tzutils.setTimezoneOffset(val, true);
            } else {
                val = null;
            }

            if (!val && !allowNullSets) {
                // Tell kendo to go back to displaying the current value.
                picker.value(curr && tzutils.localify(curr!));
                return;
            };

            // BUGFIX: Kendo fires change event even if user just blurs datepicker w/o making changes.
            // Don't write to backing value in this case.
            if (TZDate.nullableEq(val, curr)) return;

            setting = true;
            let acceptedVal : TZDate | null;
            try {
                acceptedVal = value(val as TValue);
            } finally {
                setting = false;
            }
            if (!TZDate.nullableEq(acceptedVal, val)) {
                // The model didn't like the value we tried to write. Say that the model wins
                // and go ahead and update our picker.
                picker.value(acceptedVal && tzutils.localify(acceptedVal));
            }
        },
        open: function (this : KendoPicker, e : KendoPickerOpenEvent) {
            // TH: 5/2/2016 -- Bugfix. On FF and IE, Kendo calendar and time popups were getting their z-index set to 11. Whereas in Chrome it gets set to 10002.
            // That means that on FF and IE, calendar and time popups weren't visible when launched from an element w/ a z-index higher than 11.
            // Fix is to default to the Chrome behavior, w/ an option to specify a different z-index or to let Kendo set it itself.
            let zIndex = props.popupZIndex !== undefined ? props.popupZIndex : 10002;

            // Passing 'kendo' for the popupZIndex prop means let kendo set the z-index, i.e. don't override
            if (zIndex !== 'kendo') {
                const popupView = pickerKind === PickerKind.Time || e.view === 'time' ? this.timeView! : this.dateView!;

                // At the time this open event fires, Kendo hasn't applied its z-index yet (or necessarily even created the wrapper element). Hence the timeout.
                setTimeout(function () {
                    popupView.popup.wrapper.css('z-index', zIndex);
                }, 0);
            }

            // Do time correction if this is a time picker, or if this is the time view of a datetime picker
            // NOTE: Kendo doesn't populate e.view for time-only pickers
            if (pickerKind === PickerKind.Time || e.view === 'time') {
                // TH: Set time to the nearest Kendo dropdown option.
                // The old implementation assumed hours were in 12-hour display. It broke when I switched to 24-hour display.
                const currVal = S.sample(value);

                if (currVal) {
                    const mins = (currVal as TZDate).getMinutes(),
                        // Property of kendo time picker that says how far apart dropdown options are in mins.
                        // Default is 30 mins.
                        interval = this.options.interval,
                        roundedMins = Math.round(mins / interval) * interval,
                        newVal = new TZDate(currVal as TZDate);

                    newVal.setMinutes(roundedMins);
                    // TH: 2/12/16 - This auto-rounding when we open the time dropdown was sometimes causing the date to change. E.g. if the datetime value was 2016/02/11 23:59 and the user clicked the
                    // clock button, then the time would automatically jump to 2016/02/12 00:00. That means no matter what time the user picked from the dropdown, the date would now be on 2016/02/12.
                    // BB and DC never want the date to change when you open the time dropdown.
                    // For now, solve this by turning off rounding completely. In the future, we may instead implement special handling for times between 23:45 and 23:59, e.g. 2016/02/11 23:59 might
                    // round to 2016/02/11 00:00 or 2016/02/11 23:30 instead.
                    // m.value(newVal);
                }
            }
        }
    };

    const $el = $(el);

    let picker : KendoPicker;

    if (pickerKind === PickerKind.Time) {
        picker = ($el as any).kendoTimePicker(options).data("kendoTimePicker");
    } else if (pickerKind === PickerKind.Date) {
        picker = ($el as any).kendoDatePicker(options).data("kendoDatePicker");
    } else {
        throw new Error('Unrecognized PickerKind: ' + pickerKind);
    }

    S.cleanup(() => picker.destroy());

    // TH: Annoyingly, Kendo sets some "width: 100%" style attributes that make its widgets hard to customize.
    // Override those style attributes here.
    $el.css('width', '');
    $el.closest('span.k-widget.k-header.k-input').css('width', '');

    S(() => {
        const val = value();
        // DC 09/23/24 -- CHP wanted a smoother UX for tabbing on the Add PC and the auto-focus on tabbing after the time picker caused some issues.
        // DC 09/23/24 -- Needed to "hard-code" autofocus to this input.
        if (isAddPortcall) {
            if (pickerKind === PickerKind.Date && autofocus) {
                $el.focus();
            }
            // DC 09/23/24 -- Allows a "double-tap" on the input as the user tabs, which triggers the next segement / sequence in Add PortCall.
            if (pickerKind === PickerKind.Time && val !== null) {
                $el.focus();
            }
        }
        if (!setting) picker.value(val && tzutils.localify(val!));
    });

    // format -- display for the text input portion of the date/time picker
    S(() => picker.setOptions({ format: format() }));

    // parseFormats -- other possible formats for user direct entry into the input portion of the date/time picker
    // Display will be converted to the official format on blur.
    S(() => picker.setOptions({ parseFormats: parseFormats() }));

    if (min) S(() => picker.min(tzutils.localify(min!())));
    if (max) S(() => picker.max(tzutils.localify(max!())));

    // Kendo bug fix: if we specify readonly: true in the options, it doesn't make the widget fully readonly.  it disables typing in thefield, but not the dropdown selectors.  yay.
    // Also note: We make sure to set the readonly state of the Kendo picker EVERY time we init it, not just when disabled is set to true. That's b/c if you don't explicitly set
    // the picker to readonly, Kendo will infer the readonly state based on the input element's readonly attribute. But Kendo sets the readonly attribute itself when you explicitly
    // set the readonly state of the picker, and it doesn't clear the readonly attribute when you destory the picker. So if you destroy a readonly picker, Kendo will default the next
    // picker init'ed on that same DOM element to readonly. We want our disabled prop to be definitive no matter what state the DOM was in beforehand.
    picker.readonly(disabled ?? false);
}



// WEBPACK FOOTER //
// ./apps/portcall/datetimepicker/view.tsx